import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MyPagination } from 'src/components/ui'

import { useDebounce } from '@hooks/useDebounce'
import { Skeleton } from '@mui/material'
import { useSelector } from 'react-redux'
import { EmptySection, PropertyCard } from 'src/components/elements'
import { AnnouncementModal } from 'src/components/elements/modals'
import { IProperty } from 'src/interfaces/IProperty'
import { useGetPropertiesQuery } from 'src/redux/api'
import { RootState } from 'src/redux/store'

interface ITabAllProperties {
  propertySearchId: string
  onlyFavorites?: boolean
}

const PropertiesTab: FC<ITabAllProperties> = ({ propertySearchId, onlyFavorites }) => {
  const { t } = useTranslation()
  const { search, sort } = useSelector((state: RootState) => state.propertySearch)
  const debouncedSearch = useDebounce(search, 500)

  const [currentPage, setCurrentPage] = useState(1)
  const [respondedOnly, setRespondedOnly] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<IProperty | null>(null)

  // For "all" tab, pass special value to backend
  const searchId = propertySearchId === 'all' ? 'all' : propertySearchId

  const {
    data: { properties, totalPages } = { properties: [] },
    isLoading,
    isError,
  } = useGetPropertiesQuery(
    {
      propertySearchId: searchId,
      search: debouncedSearch,
      page: currentPage,
      sort,
      limit: 8,
      onlyFavorites,
      responded: respondedOnly || undefined,
    },
    { refetchOnMountOrArgChange: true },
  )

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleOpenModal = (property: IProperty) => {
    setSelectedProperty(property)
    setOpenModal(true)
  }

  if (isError) {
    return <span>{t('errors.NETWORK')}</span>
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        {[
          { value: false, label: t('all') },
          { value: true, label: t('property.responded_filter') },
        ].map((option) => (
          <button
            key={String(option.value)}
            onClick={() => {
              setRespondedOnly(option.value)
              setCurrentPage(1)
            }}
            className={
              respondedOnly === option.value
                ? 'px-3 min-h-9 text-sm font-extrabold rounded-full border border-solid border-brand-strong bg-brand-soft text-brand-ink transition-colors duration-200'
                : 'px-3 min-h-9 text-sm font-bold rounded-full border border-solid border-ink/[0.18] text-ink hover:border-brand transition-colors duration-200'
            }
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* The request already exists (this tab belongs to a saved search), so
          the empty state is "no matches yet" — never "create your first
          request", which made no sense here (owner feedback). */}
      {!isLoading && properties.length === 0 && (
        <EmptySection
          title={t('emptyProposalsForRequest.title')}
          description={t('emptyProposalsForRequest.description')}
        />
      )}

      {/* a carousel on phones, a grid from md */}
      <div className="flex gap-3 overflow-x-auto snap-x -mx-4 px-4 scroll-px-4 pb-2 md:grid md:grid-cols-4 xl:grid-cols-5 md:gap-4 md:overflow-visible md:mx-0 md:px-0 md:pb-0">
        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={397} />
            <Skeleton variant="rounded" height={397} />
            <Skeleton variant="rounded" height={397} />
            <Skeleton variant="rounded" height={397} />
          </>
        ) : (
          properties.map((property) => (
            <PropertyCard
              onClick={() => handleOpenModal(property)}
              key={property.id}
              property={property}
              className="shrink-0 w-[16.5rem] snap-start md:w-auto"
            />
          ))
        )}

        {selectedProperty && (
          <AnnouncementModal
            open={openModal}
            property={selectedProperty}
            handleClose={() => setOpenModal(false)}
          />
        )}
      </div>
      <MyPagination
        totalPages={totalPages || 0}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

export default PropertiesTab
